import * as THREE from 'three'

export type DebugMaterialMode = 'authentic' | 'type_colors' | 'mesh_normals' | 'wireframe_normals' | 'uv_grid'

export type ObjectMeshType =
  | 'wall'
  | 'floor'
  | 'ceiling'
  | 'door'
  | 'elevator'
  | 'destructible'
  | 'target'
  | 'pillar'
  | 'trim'
  | 'enemy'

export interface MeshMetadata {
  id?: string
  name?: string
  sourceTag?: string
  dimensions?: [number, number, number]
  availableSwatches?: string[]
  getDetails?: () => Record<string, string | number | boolean | undefined>
}

export interface RegisteredEntry {
  mesh: THREE.Mesh
  type: ObjectMeshType
  originalMaterial: THREE.Material | THREE.Material[]
  typeMaterial: THREE.Material
  normalMaterial: THREE.Material
  wireframeMaterial: THREE.Material
  uvMaterial: THREE.Material
  meta?: MeshMetadata
}

export interface DebugMaterialRegistry {
  mode: DebugMaterialMode
  setMode: (mode: DebugMaterialMode) => void
  cycleMode: () => DebugMaterialMode
  registerMesh: (
    mesh: THREE.Mesh,
    type: ObjectMeshType,
    originalMat: THREE.Material | THREE.Material[],
    meta?: MeshMetadata,
  ) => void
  /** Update the stored original material snapshot for a mesh.
   *  Call this whenever an external operation (e.g. door lock swap,
   *  hot-reload material replacement) changes `mesh.material` so that
   *  setMode('authentic') restores the correct, up-to-date material. */
  updateOriginalMaterial: (mesh: THREE.Mesh, newMat: THREE.Material | THREE.Material[]) => void
  updateAll: () => void
  updateMeshGeometry: (mesh: THREE.Mesh) => void
  getEntryByMesh: (mesh: THREE.Mesh) => RegisteredEntry | undefined
  getEntriesByType: (type: ObjectMeshType) => RegisteredEntry[]
  getAllMeshes: () => THREE.Mesh[]
}

export const TYPE_COLORS: Record<ObjectMeshType, { color: number; emissive?: number; label: string }> = {
  wall: { color: 0x3d5a80, emissive: 0x0c1520, label: 'Wall' },          // Steel Blue
  floor: { color: 0x52796f, emissive: 0x142820, label: 'Floor' },        // Distinct Sage / Olive Concrete
  ceiling: { color: 0x1a1c23, emissive: 0x040507, label: 'Ceiling' },    // Dark Obsidian Charcoal
  door: { color: 0xff007f, emissive: 0x440022, label: 'Sliding Door' },  // Bright Fuchsia / Magenta
  elevator: { color: 0x00e5ff, emissive: 0x003344, label: 'Elevator' },  // Neon Cyan
  destructible: { color: 0xff9800, emissive: 0x332000, label: 'Crate / Prop' }, // Bright Amber
  target: { color: 0xff1744, emissive: 0x440011, label: 'Range Target' }, // Bright Crimson Red
  pillar: { color: 0x8e24aa, emissive: 0x220033, label: 'Pillar / Column' }, // Purple Slate
  trim: { color: 0x00e676, emissive: 0x003311, label: 'Floor Trim' },    // Vibrant Spring Green
  enemy: { color: 0xff3366, emissive: 0x440011, label: 'Enemy / Billboard' }, // Vibrant Neon Red-Pink
}

function createUVDiagnosticTexture(): THREE.Texture {
  if (typeof document === 'undefined') {
    return new THREE.Texture()
  }
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 512
  const ctx = canvas.getContext('2d')
  if (!ctx) return new THREE.CanvasTexture(canvas)

  // 8x8 checkerboard background
  const size = 512 / 8
  for (let y = 0; y < 8; y++) {
    for (let x = 0; x < 8; x++) {
      const isEven = (x + y) % 2 === 0
      ctx.fillStyle = isEven ? '#1e293b' : '#334155'
      ctx.fillRect(x * size, y * size, size, size)
    }
  }

  // Draw UV Grid Subdivisions (8x8 colored grid lines)
  ctx.strokeStyle = 'rgba(0, 240, 255, 0.6)'
  ctx.lineWidth = 2
  for (let i = 0; i <= 8; i++) {
    ctx.beginPath()
    ctx.moveTo(i * size, 0)
    ctx.lineTo(i * size, 512)
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(0, i * size)
    ctx.lineTo(512, i * size)
    ctx.stroke()
  }

  // Outer Border
  ctx.strokeStyle = '#00f0ff'
  ctx.lineWidth = 8
  ctx.strokeRect(4, 4, 504, 504)

  // UV Quadrant & Center Labels
  ctx.font = 'bold 24px monospace'
  ctx.fillStyle = '#f8fafc'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('UV (0.5, 0.5)', 256, 256)

  // Coordinate corners
  ctx.font = 'bold 18px monospace'
  ctx.fillStyle = '#38bdf8'
  ctx.textAlign = 'left'
  ctx.fillText('(0,0)', 16, 28)
  ctx.textAlign = 'right'
  ctx.fillText('(1,0)', 496, 28)
  ctx.textAlign = 'left'
  ctx.fillText('(0,1)', 16, 490)
  ctx.textAlign = 'right'
  ctx.fillText('(1,1)', 496, 490)

  // Direction Arrows
  ctx.fillStyle = '#ff007f'
  ctx.font = 'bold 16px monospace'
  ctx.textAlign = 'center'
  ctx.fillText('+U →', 256, 40)
  ctx.fillText('+V ↓', 470, 256)

  const texture = new THREE.CanvasTexture(canvas)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  return texture
}

export function createDebugMaterialRegistry(): DebugMaterialRegistry {
  const entries: RegisteredEntry[] = []
  const meshToEntryMap = new Map<THREE.Mesh, RegisteredEntry>()
  let currentMode: DebugMaterialMode = 'authentic'
  const uvTexture = createUVDiagnosticTexture()

  const sharedUvMaterial = new THREE.MeshStandardMaterial({
    map: uvTexture,
    roughness: 0.5,
    metalness: 0.1,
  })

  function createMaterialsForType(type: ObjectMeshType, originalMat?: THREE.Material | THREE.Material[]): {
    typeMaterial: THREE.Material
    normalMaterial: THREE.Material
    wireframeMaterial: THREE.Material
    uvMaterial: THREE.Material
  } {
    const info = TYPE_COLORS[type]
    const orig = Array.isArray(originalMat) ? originalMat[0] : originalMat
    const isTransparent = (orig as any)?.transparent ?? false
    const origMap = (orig as any)?.map

    const typeMaterial = new THREE.MeshStandardMaterial({
      color: origMap ? 0xffffff : info.color,
      map: origMap || null,
      transparent: isTransparent,
      alphaTest: (orig as any)?.alphaTest ?? (isTransparent ? 0.45 : 0),
      emissive: info.emissive ?? 0x000000,
      roughness: 0.4,
      metalness: 0.3,
      side: (orig as any)?.side ?? THREE.FrontSide,
    })

    const normalMaterial = new THREE.MeshNormalMaterial({
      transparent: isTransparent,
      side: (orig as any)?.side ?? THREE.FrontSide,
    })

    const wireframeMaterial = new THREE.MeshNormalMaterial({
      wireframe: true,
      transparent: isTransparent,
      side: (orig as any)?.side ?? THREE.FrontSide,
    })

    return { typeMaterial, normalMaterial, wireframeMaterial, uvMaterial: sharedUvMaterial }
  }

  function applyMaterial(entry: RegisteredEntry, mode: DebugMaterialMode) {
    switch (mode) {
      case 'authentic':
        entry.mesh.material = entry.originalMaterial
        break
      case 'type_colors':
        entry.mesh.material = entry.typeMaterial
        break
      case 'mesh_normals':
        entry.mesh.material = entry.normalMaterial
        break
      case 'wireframe_normals':
        entry.mesh.material = entry.wireframeMaterial
        break
      case 'uv_grid':
        entry.mesh.material = entry.uvMaterial
        break
    }
  }

  function setMode(mode: DebugMaterialMode) {
    currentMode = mode
    for (const entry of entries) {
      applyMaterial(entry, currentMode)
    }
  }

  function cycleMode(): DebugMaterialMode {
    const modes: DebugMaterialMode[] = ['authentic', 'type_colors', 'mesh_normals', 'wireframe_normals', 'uv_grid']
    const nextIdx = (modes.indexOf(currentMode) + 1) % modes.length
    setMode(modes[nextIdx])
    return currentMode
  }

  function registerMesh(
    mesh: THREE.Mesh,
    type: ObjectMeshType,
    originalMat: THREE.Material | THREE.Material[],
    meta?: MeshMetadata,
  ) {
    const { typeMaterial, normalMaterial, wireframeMaterial, uvMaterial } = createMaterialsForType(type, originalMat)
    const entry: RegisteredEntry = {
      mesh,
      type,
      originalMaterial: originalMat,
      typeMaterial,
      normalMaterial,
      wireframeMaterial,
      uvMaterial,
      meta,
    }
    entries.push(entry)
    meshToEntryMap.set(mesh, entry)
    if (currentMode !== 'authentic') {
      applyMaterial(entry, currentMode)
    }
  }

  function updateAll() {
    for (const entry of entries) {
      applyMaterial(entry, currentMode)
    }
  }

  function getEntryByMesh(mesh: THREE.Mesh): RegisteredEntry | undefined {
    return meshToEntryMap.get(mesh)
  }

  function getEntriesByType(type: ObjectMeshType): RegisteredEntry[] {
    return entries.filter((e) => e.type === type)
  }

  function updateOriginalMaterial(mesh: THREE.Mesh, newMat: THREE.Material | THREE.Material[]) {
    const entry = meshToEntryMap.get(mesh)
    if (!entry) return
    entry.originalMaterial = newMat
    if (currentMode === 'authentic') {
      mesh.material = newMat
    }
  }

  function updateMeshGeometry(mesh: THREE.Mesh) {
    const entry = meshToEntryMap.get(mesh)
    if (entry) {
      // Refresh dimensions from live bounding box if available
      const bbox = mesh.geometry?.boundingBox
      if (bbox && entry.meta) {
        entry.meta.dimensions = [
          bbox.max.x - bbox.min.x,
          bbox.max.y - bbox.min.y,
          bbox.max.z - bbox.min.z,
        ]
      }
      applyMaterial(entry, currentMode)
    }
  }

  function getAllMeshes(): THREE.Mesh[] {
    return entries.map((e) => e.mesh)
  }

  return {
    get mode() {
      return currentMode
    },
    setMode,
    cycleMode,
    registerMesh,
    updateOriginalMaterial,
    updateMeshGeometry,
    updateAll,
    getEntryByMesh,
    getEntriesByType,
    getAllMeshes,
  }
}
